import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useDocumentos, type Documento } from '@hooks/useDocumentos.js'
import { useLancamentos } from '@hooks/useLancamentos.js'
import { DocumentosPageSkeleton, PageHeaderSkeleton } from '@components/primitives/Skeleton.js'
import { PageShell } from '@components/layout/PageShell.js'
import { ModePageHeader } from '@components/layout/ModePageHeader.js'
import { ConfirmDeleteModal } from '@components/documentos/ConfirmDeleteModal.js'
import { DocumentoFormModal } from '@components/documentos/DocumentoFormModal.js'
import { DocumentosContent } from '@components/documentos/DocumentosContent.js'
import { DocumentosFolders } from '@components/documentos/DocumentosFolders.js'
import { DocumentosStats } from '@components/documentos/DocumentosStats.js'
import { DocumentosToolbar } from '@components/documentos/DocumentosToolbar.js'
import { PreviewModal } from '@components/documentos/PreviewModal.js'
import {
  EMPTY_FORM,
  type DocItem,
  type FormState,
  type ViewMode,
} from '@components/documentos/types.js'
import { docToFormState, pickFiles } from '@components/documentos/utils.js'

interface NavPrefill {
  prefillDoc?: Partial<FormState>
}

export function DocumentosPage() {
  const location = useLocation()
  const { documentos, stats, isLoading, add, update, remove } = useDocumentos()
  const { lancamentos } = useLancamentos()

  const [viewMode, setViewMode] = useState<ViewMode>('gallery')
  const [search, setSearch] = useState('')
  const [catFiltro, setCatFiltro] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<DocItem | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<DocItem | null>(null)
  const [previewDoc, setPreviewDoc] = useState<DocItem | null>(null)
  const [formData, setFormData] = useState<FormState>(EMPTY_FORM)
  const [importing, setImporting] = useState(false)

  useEffect(() => {
    const state = location.state as NavPrefill | null
    if (!state?.prefillDoc) return
    setEditTarget(null)
    setFormData({ ...EMPTY_FORM, ...state.prefillDoc })
    setFormOpen(true)
    window.history.replaceState({}, document.title)
  }, [location.state])

  if (isLoading) {
    return (
      <PageShell>
        <PageHeaderSkeleton label='Carregando arquivos…' />
        <DocumentosPageSkeleton label='Carregando arquivos…' />
      </PageShell>
    )
  }

  const docs = documentos as DocItem[]
  const q = search.trim().toLowerCase()
  const filtered = docs.filter((d) => {
    const matchSearch =
      !q ||
      d.nome.toLowerCase().includes(q) ||
      (d.observacoes ?? '').toLowerCase().includes(q) ||
      (d.fornecedor ?? '').toLowerCase().includes(q)
    const matchCat = !catFiltro || d.categoria === catFiltro
    return matchSearch && matchCat
  })

  const openNew = () => {
    setEditTarget(null)
    setFormData(EMPTY_FORM)
    setFormOpen(true)
  }

  const openEdit = (doc: DocItem) => {
    setEditTarget(doc)
    setFormData(docToFormState(doc))
    setFormOpen(true)
  }

  const handleSave = async (form: FormState) => {
    const payload: Omit<Documento, 'id' | 'criadoEm'> = {
      nome: form.nome.trim(),
      categoria: form.categoria,
      tipo: form.tipo || 'application/octet-stream',
      data: form.data,
      ...(form.tamanho > 0 && { tamanho: form.tamanho }),
      ...(form.fornecedor.trim() && { fornecedor: form.fornecedor.trim() }),
      ...(form.valor && Number(form.valor) > 0 && { valor: Number(form.valor) }),
      ...(form.observacoes.trim() && { observacoes: form.observacoes.trim() }),
      ...(form.moduloId && { moduloId: form.moduloId }),
      ...(form.lancamentoId && { lancamentoId: Number(form.lancamentoId) }),
      hasArquivo: Boolean(form.arquivo || form.hasExistingArquivo),
    }
    if (editTarget) {
      await update(editTarget.id, payload, form.arquivo)
    } else {
      await add(payload, form.arquivo)
    }
    setFormOpen(false)
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    await remove(deleteTarget.id)
    setDeleteTarget(null)
  }

  const handleBatchImport = async () => {
    setImporting(true)
    const files = await pickFiles(true)
    for (const file of files) {
      await add(
        {
          nome: file.name,
          categoria: 'Outros',
          tipo: file.type || 'application/octet-stream',
          tamanho: file.size,
          hasArquivo: true,
          data: new Date().toISOString().slice(0, 10),
        },
        file,
      )
    }
    setImporting(false)
  }

  const lancamentosSimples = (lancamentos as { id: number; titulo: string }[])
    .filter((l) => l.id != null)
    .map((l) => ({ id: l.id, titulo: l.titulo }))

  return (
    <PageShell>
      <ModePageHeader
        title='Arquivos da obra'
        subtitle='Contratos, NFs, projetos e laudos. Foto do dia a dia fica no Diário.'
      />

      <DocumentosStats stats={stats} total={documentos.length} />

      <DocumentosFolders documentos={docs} catFiltro={catFiltro} onCatFiltroChange={setCatFiltro} />

      <DocumentosToolbar
        search={search}
        catFiltro={catFiltro}
        viewMode={viewMode}
        importing={importing}
        onSearchChange={setSearch}
        onCatFiltroChange={setCatFiltro}
        onViewModeChange={setViewMode}
        onNew={openNew}
        onBatchImport={handleBatchImport}
      />

      <DocumentosContent
        filtered={filtered}
        totalCount={documentos.length}
        viewMode={viewMode}
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        onPreview={setPreviewDoc}
        onNew={openNew}
        onClearFilters={() => {
          setSearch('')
          setCatFiltro('')
        }}
      />

      <DocumentoFormModal
        key={editTarget?.id ?? 'new'}
        open={formOpen}
        initial={formData}
        title={editTarget ? 'Editar Documento' : 'Novo Documento'}
        onSave={handleSave}
        onClose={() => setFormOpen(false)}
        lancamentos={lancamentosSimples}
      />

      <PreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />

      {deleteTarget && (
        <ConfirmDeleteModal
          open
          nome={deleteTarget.nome}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </PageShell>
  )
}
